<!--
	@component
	person/company autocomplete for the FAK form.
	uses AutocompleteInputField with `valueAsObject: true` — `contactPerson` IS the value object.

	@param {PersonAndCompanyData | null} contactPerson - bindable. the selected person object, used as svelecte value directly.
	@param {string | null} personLink - bindable. manual link input shown when autocomplete is toggled off.
	@param {string | null} onboardingComment - bindable. comment field shown for new entities.
	@param {boolean} isEditable - bindable. controls disabled state of all fields.
	@param {CompanyData | null} company - company data merged into contactPerson.text on mount and selection change.
	@param {FontProps} fontProps - required. font styling for all fields.
	@param {string} errorPath - required. base path for form validation errors.
	@param {() => void} onChange - callback fired after selection change + company merge.

	@see AddressDataAutocomplete for the address counterpart.
	@see AutocompleteInputField for the underlying svelecte wrapper.
-->
<script lang="ts">
	import AutocompleteInputField from './AutocompleteInputField.svelte'
	import type { AutocompleteFieldConfig } from '../types/autocompleteFieldConfig'
	import { getFormAcConfigFetch } from '../types/autocompleteFieldConfig'
	import { t } from '../../i18n/i18n'
	import type { FontProps } from '../types/fontProps'
	import InputField from '../molecules/InputField.svelte'
	import { inputFieldFormats } from '../utils/formatters'
	import SecondaryButton, { SecondaryBtnColors } from '../atoms/SecondaryButton.svelte'
	import { onMount } from 'svelte'
	import { createTippy } from 'svelte-tippy'
	import type { CompanyData } from '../types/companyData'
	import type { PersonAndCompanyData } from '../types/personAndCompanyData'
	import { copyPersonAndCompanyData } from '../types/personAndCompanyData'
	import { clearFormErrors } from '../map/fak/utils/fakUtils'

	let {
		contactPerson = $bindable(null),
		personLink = $bindable(null),
		onboardingComment = $bindable(null),
		isEditable = $bindable(false),
		company = null,
		fontProps,
		editView = { canEditOthers: true, canEditPhone: true },
		required = false,
		errorPath,
		showCompany = true,
		// FIXME: outerId was a Svelte 4 reactive dependency trigger — needs $effect or removal
		outerId = '',
		isNew = true,
		personInputId = 'person-input-id' + Math.random().toString(16).slice(2),
		companyInputId = 'company-input-id' + Math.random().toString(16).slice(2),
		onChange
	}: {
		contactPerson?: PersonAndCompanyData | null
		personLink?: string | null
		onboardingComment?: string | null
		isEditable?: boolean
		company?: CompanyData | null
		fontProps: FontProps
		editView?: { canEditOthers: boolean; canEditPhone: boolean }
		required?: boolean
		errorPath: string
		showCompany?: boolean
		outerId?: string // used to detect if another lot or project is selected
		isNew?: boolean
		personInputId?: string
		companyInputId?: string
		onChange?: (() => void) | undefined
	} = $props()
	const contactPersonAutocompleteConfig: AutocompleteFieldConfig = {
		...getFormAcConfigFetch('person-data', false, 'id', 'text', !isEditable),
		valueAsObject: true
	}

	// reset autocomplete when the outer entity (lot/project) changes
	$effect(() => {
		if (outerId) showAutocomplete = true
	})

	let contactPersonId = $derived(contactPerson?.id ?? null)
	let options = $derived(contactPerson?.lastName ? [copyPersonAndCompanyData(contactPerson)] : [])
	let companyString = $derived.by(() => {
		if (contactPerson && contactPerson.companyName && contactPerson.address) {
			return (
				contactPerson.companyName +
				', ' +
				contactPerson.address.zip +
				' ' +
				contactPerson.address.street
			)
		}
		return ''
	})
	let isPersonLinkEditable = $derived(personLink == null || personLink === '')
	let showAutocomplete: boolean = $state(true)

	onMount(() => {
		updateCompanyInPerson()
	})

	function updateCompanyInPerson() {
		if (company?.companyName && contactPerson?.text) {
			if (!contactPerson.text.includes(',')) {
				contactPerson.text = contactPerson.text + ', ' + company.companyName
			}
			contactPerson.companyName = company.companyName
			contactPerson.address = { ...company.address }
			contactPerson.showCompany = true
		} else if (contactPerson) {
			contactPerson.showCompany = false
		}
	}

	function autocompleteValueChanged() {
		updateCompanyInPerson()
		clearFormErrors()
		onChange?.()
	}

	function toggle() {
		showAutocomplete = !showAutocomplete
	}

	function togglePerson() {
		if (isEditable && !(contactPerson && contactPerson.lastName)) {
			toggle()
		}
	}

	// Use the appendTo to force placement: 'right', which would otherwise be overwritten by popper option 'flip'
	const disabledTippy = createTippy({
		animation: 'scale-extreme',
		content: $t('UI.map.contactPerson.disabled.tt'),
		delay: [100, 200],
		hideOnClick: false,
		interactive: true,
		maxWidth: 367,
		placement: 'left',
		theme: 'mmttip',
		appendTo: document.getElementsByClassName('fak-base-layout')[0]
	})
	const filledTippy = createTippy({
		animation: 'scale-extreme',
		content: $t('UI.map.contactPerson.filled.tt'),
		delay: [100, 200],
		hideOnClick: false,
		interactive: true,
		maxWidth: 367,
		placement: 'left',
		theme: 'mmttip',
		appendTo: document.getElementsByClassName('fak-base-layout')[0]
	})
	const addTippy = createTippy({
		animation: 'scale-extreme',
		content: $t('UI.map.contactPerson.create.tt'),
		delay: [100, 200],
		hideOnClick: false,
		interactive: true,
		maxWidth: 367,
		placement: 'left',
		theme: 'mmttip',
		appendTo: document.getElementsByClassName('fak-base-layout')[0]
	})
	const linkTippy = createTippy({
		animation: 'scale-extreme',
		content: $t('UI.map.contactPerson.link.tt'),
		delay: [100, 200],
		hideOnClick: 'toggle',
		interactive: true,
		maxWidth: 367,
		placement: 'right',
		theme: 'mmttip',
		appendTo: document.getElementsByClassName('fak-base-layout')[0]
	})

	let disabled = $derived(!isEditable)
</script>

<div class="autocomplete-grid">
	{#if showCompany}
		{#key contactPersonId}
			<InputField
				bind:value={companyString}
				id={companyInputId}
				format={inputFieldFormats.FULL}
				placeholder={$t('UI.Model.company')}
				{fontProps}
				readOnly
				errorPath={errorPath + '.*'}
			/>
		{/key}
	{/if}
	{#if showAutocomplete}
		{#key contactPersonId}
			<AutocompleteInputField
				bind:value={contactPerson}
				{options}
				{disabled}
				id={personInputId}
				{required}
				{fontProps}
				autocompleteConfig={contactPersonAutocompleteConfig}
				label={$t('UI.map.project.contactPerson')}
				errorPath={errorPath + '.*'}
				onChange={autocompleteValueChanged}
			>
				<!-- this should trigger an error for the subfields, too -->
				{#snippet controlEnd()}
					<div class="control-end" onclick={togglePerson}>
						{#if contactPerson}
							<div class="separator"></div>
						{/if}
						{#if !isEditable}
							<div class="disabled-icon" use:disabledTippy>
								<div class="add-icon-20"></div>
							</div>
						{:else if contactPerson && contactPerson.lastName}
							<div class="disabled-icon" use:filledTippy>
								<div class="add-icon-20"></div>
							</div>
						{:else}
							<div class="green-icon" use:addTippy>
								<div class="add-icon-20"></div>
							</div>
						{/if}
					</div>
				{/snippet}
			</AutocompleteInputField>
		{/key}
	{:else}
		<div class="person-link-field" use:linkTippy>
			<InputField
				bind:value={personLink}
				format={inputFieldFormats.FULL}
				placeholder={$t('UI.contactPerson.link.ph')}
				readOnly={!editView.canEditOthers || !isPersonLinkEditable}
				errorPath={errorPath + '.personLink'}
				required
			/>
		</div>
		{#if isNew}
			<InputField
				bind:value={onboardingComment}
				format={inputFieldFormats.FULL}
				placeholder={$t('Lot.onboardingComment')}
				readOnly={!editView.canEditOthers || !isPersonLinkEditable}
			/>
		{/if}
		<div class="discard-button-row">
			<SecondaryButton
				color={SecondaryBtnColors.RED}
				label={$t('UI.button.dismiss')}
				paddingHorizontal="1.375rem"
				paddingTop="0.875rem"
				sizeAdaptingToText
				on:click={toggle}
			/>
		</div>
	{/if}
</div>

<style lang="scss">
	@import '$styles/global';
	@import '$styles/autocomplete';

	.autocomplete-grid {
		width: 100%;
		display: grid;
		grid-template-columns: 1fr;
		row-gap: 0.938rem;
	}

	.discard-button-row {
		@include flex-row(0.375rem, center, flex-end);
	}

	.control-end {
		@include flex-row(0.375rem);

		margin-right: 0.25rem;

		& .separator {
			width: 1px;
			background: $grey-700;
			display: block;
			height: 1.25rem;
		}
	}
</style>
